/**
 * Estimation du port d'un devis, règle pour règle avec App\Services\Trade\ShippingEstimate :
 * franco atteint, sinon grille au poids, sinon forfait.
 *
 * @see docs/DRI/Phase3/poids-transport.md § 6
 */

export const GRID_REASONS = {
    no_lines: 'aucun article pesé',
    incomplete: 'poids incomplet',
    no_zone: 'zone inconnue',
    no_bracket: 'aucune tranche',
    on_quote: 'tranche sur devis',
};

function isOn(value) {
    return value === true || value === 1 || value === '1';
}

function round(value, decimals) {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
}

function grid(context, weight, counted, complete) {
    if (counted === 0) return [null, null, 'no_lines'];
    if (!complete) return [null, null, 'incomplete'];
    if (!context.zone) return [null, null, 'no_zone'];

    const bracket = (context.brackets || []).find(b => b.max_weight === null || weight <= b.max_weight + 0.0005);
    if (!bracket) return [null, null, 'no_bracket'];

    const found = { label: bracket.label, mode: bracket.mode };

    return bracket.amount === null ? [found, null, 'on_quote'] : [found, parseFloat(bracket.amount), null];
}

/** L'estimation d'un jeu de lignes ; weights donne le poids unitaire de chaque article, null s'il manque. */
export function estimateShipping(lines, context, weights) {
    let goods = 0;
    let weight = 0;
    let counted = 0;
    const missing = [];

    lines.forEach(line => {
        if (isOn(line.is_title) || isOn(line.is_eco_participation) || isOn(line.is_shipping)) return;

        goods += parseFloat(line.total_after_discount) || 0;

        if (!line.article_id || String(line.unit || '').trim().toLowerCase() === 'forfait') return;

        const unitWeight = weights[line.article_id];
        if (unitWeight === null || unitWeight === undefined) {
            missing.push(String(line.reference || '').trim() || line.designation || '');
            return;
        }

        weight += parseFloat(unitWeight) * (parseFloat(line.quantity) || 0);
        counted++;
    });

    goods = round(goods, 2);
    weight = round(weight, 3);
    const complete = counted > 0 && missing.length === 0;
    const [bracket, gridAmount, gridReason] = grid(context, weight, counted, complete);

    const conditions = context.conditions || {};
    const threshold = conditions.free_shipping_amount;
    const flatRate = conditions.shipping_flat_rate;
    const freeShipping = threshold === null || threshold === undefined ? null : {
        threshold: threshold,
        reached: goods >= threshold,
        shortfall: Math.max(0, round(threshold - goods, 2)),
    };

    let port, source, reason;
    if (freeShipping && freeShipping.reached) {
        [port, source, reason] = [0, 'free', null];
    } else if (gridAmount !== null) {
        [port, source, reason] = [gridAmount, 'grid', null];
    } else if (flatRate !== null && flatRate !== undefined) {
        [port, source, reason] = [flatRate, 'flat', GRID_REASONS[gridReason] || null];
    } else {
        [port, source, reason] = [null, 'none', GRID_REASONS[gridReason] || null];
    }

    return {
        goods, weight, counted, missing, complete,
        zone: context.zone || null,
        location: context.location || null,
        bracket,
        grid_amount: gridAmount,
        grid_reason: gridReason,
        free_shipping: freeShipping,
        port, source, reason,
        weekday: conditions.free_shipping_weekday || null,
    };
}
